import { Alert, AlertIcon, AlertTitle, Box, Button, Divider, HStack, Icon, Image, Modal, ModalBody, ModalContent, ModalOverlay, Radio, Stack, Text, useDisclosure, useOutsideClick } from '@chakra-ui/react'
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateFormControl, Ellipsis, InputFormControl, RadioGroupFormControl } from 'BrokerTsLib/src/components'
import type { SendContractEmailRequest, SendContractEmailResponse } from 'BrokerTsLib/src/dtos'
import { ContractAcceptationType, type ContractSignOption, ContractSignOptions } from 'BrokerTsLib/src/enums'
import { useCustomToast } from 'BrokerTsLib/src/hooks/useCustomToast'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useRef } from 'react'
import root from 'react-shadow'
import * as yup from 'yup'

import { Loading } from '@/components/Loading'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useGetContractEmail, useSendContractEmail } from '@/hooks/useMailing'
import type { AxiosError } from 'axios'

interface SendContractContextProps {
  paramsId?: string
  insuranceId?: number
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

const SendContractContext = createContext<SendContractContextProps | null>(null)

export const SendContractContextProvider = ({ children, insuranceId }: { children: ReactNode, insuranceId?: number }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { insuranceParams } = useInsuranceParams()

  return <SendContractContext.Provider value={{
    paramsId: insuranceParams?.generalParams.paramsId ?? undefined,
    insuranceId: insuranceId ?? insuranceParams?.generalParams.insuranceId ?? undefined,
    isOpen,
    onOpen,
    onClose
  }}>
    {children}
  </SendContractContext.Provider>
}

export const useSendContractContext = () => {
  const context = useContext(SendContractContext)
  if (!context) {
    throw new Error('useSendContractContext must be used within a SendContractContextProvider')
  }
  return context
}

export const SendContractModal = () => {
  const { paramsId, insuranceId, isOpen, onClose } = useSendContractContext()
  const { data, isLoading, isSuccess, isError } = useGetContractEmail(paramsId, insuranceId, isOpen)
  const { mutateAsync: sendAsync } = useSendContractEmail()
  const { successToast, toast } = useCustomToast()

  const send = useCallback((v: SendContractFormValues) => sendAsync({ ...v, paramsId: paramsId!, insuranceId: insuranceId! })
    .then(r => {
      onClose()
      successToast('E-mail byl úspěšně odeslán')(r)
    })
    .catch((r: AxiosError<SendContractEmailResponse>) => {
      toast({
        status: 'error',
        title: 'Nepodařilo se odeslat e-mail',
        description: r.response?.data.errors?.join(', ')
      })
    })
  , [sendAsync, successToast, toast, onClose, paramsId, insuranceId])

  const bodyRef = useRef<HTMLDivElement>(null)

  useOutsideClick({
    ref: bodyRef,
    handler: (e) => {
      const clickedElementIsInsideContent = bodyRef.current?.contains(e.target as Node)
      const isChildOfFlatpickr = (child: Node | null): boolean => {
        let node = child
        while (node) {
          if (node instanceof HTMLElement && node.classList.contains('flatpickr-calendar')) {
            return true
          }
          node = node.parentNode
        }
        return false
      }


      if (!clickedElementIsInsideContent && !isChildOfFlatpickr(e.target as Node))
        onClose()
    }
  })

  return <Modal isOpen={isOpen} onClose={() => { }} size='full'>
    <ModalOverlay />
    <ModalContent bg='transparent' boxShadow='none'>
      <ModalBody px={{ base: 2, md: 4 }} py={{ base: 2, md: 6 }} alignContent='center'>
        <Box ref={bodyRef} mx='auto' w='100%' maxW={737} >
          {isLoading && <Loading mt={14} large spinnerProps={{ size: 'lg', thickness: '3px' }} color='primary.300' fontSize='lg' />}
          {isSuccess && <SendContractForm {...data} onSubmit={send} />}
          {isError && <Alert status="error" mt={14} as={Stack} alignContent='start' >
            <HStack>
              <AlertIcon />
              <AlertTitle>
                Nepodařilo se načíst data.
              </AlertTitle>
            </HStack>
          </Alert>}
        </Box>
      </ModalBody>
    </ModalContent>
  </Modal >
}

const sendContractSchema = yup.object({
  email: yup.string().required('Je nutné vyplnit e-mailovou adresu příjemce.').matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Neplatná e-mailová adresa.'),
  signOption: yup.mixed<ContractSignOption>(),
  dateOfSign: yup.date(),
})

type SendContractFormValues = Required<Pick<SendContractEmailRequest, 'email'>> & Omit<SendContractEmailRequest, 'paramsId' | 'insuranceId' | 'email'>
const SendContractForm = ({ subject, logoUrl, email, header, recap, acceptation, documents, signature, disclaimer, onSubmit }: {
  subject: string
  logoUrl: string
  email: string
  header: string,
  recap: string,
  acceptation: string,
  documents: string,
  signature: string,
  disclaimer: string
  onSubmit: (data: SendContractFormValues) => Promise<void>
}) => {
  const methods = useForm<SendContractFormValues>({
    defaultValues: {
      email: email ?? ''
    },
    resolver: yupResolver(sendContractSchema),
  })
  const { insuranceParams } = useInsuranceParams()

  const { handleSubmit, formState: { isSubmitting, isValid }, control } = methods

  return (
    <FormProvider {...methods}>
      <Stack as="form" gap={{ base: 3, md: 6 }} onSubmit={handleSubmit(onSubmit)}>
        <Stack padding="16px 24px 24px 24px" bg='white' borderRadius='10px' boxShadow='0 0 16px 0 rgba(0, 0, 0, 0.1)' border="1px solid #e5e7e9;">
          <HStack w='full' gap={6} alignItems='end'>
            <InputFormControl name="email" control={control}
              label="E-mailová adresa příjemce"
              placeholder='jan.novak@email.cz'
              isRequired
              w='full'
              flexGrow={1} />
            <Button colorScheme="primary" type="submit" size='lg' isLoading={isSubmitting} px={8} isDisabled={!isValid}>
              <HStack gap={4}>
                <Icon as={FontAwesomeIcon} icon={faPaperPlane} />
                <span>Odeslat</span>
              </HStack>
            </Button>
          </HStack>
          <Text fontSize='xs' color='gray.500'>
            Na e-mailovou adresu příjemce bude odeslána zpráva s rekapitulací pojištění a dokumenty k pojištění.<br />
          </Text>
        </Stack>

        <Stack gap={0} padding="16px 24px 24px 24px" bg='white' borderRadius='10px' boxShadow='0 0 16px 0 rgba(0, 0, 0, 0.1)' border="1px solid #e5e7e9;" pos='relative'>
          <Ellipsis tooltipProps={{ maxW: 'xl' }} mt={2}>
            <Text fontSize='lg' fontWeight='semibold'>{subject}</Text>
          </Ellipsis>
          <Divider my={4} />

          <Box textAlign='center' pb={8} pt={2.5}>
            <Image src={logoUrl} alt="Logo" maxW={170} />
          </Box>

          <root.div>
            <Box dangerouslySetInnerHTML={{ __html: header }} />
          </root.div>

          <root.div>
            <Box dangerouslySetInnerHTML={{ __html: recap }} />
          </root.div>

          {(insuranceParams?.generalParams.contractAcceptationType === ContractAcceptationType.Signature) ? <Stack>
            <Text fontWeight='bold' color='black' fontSize='11pt'>Platební údaje</Text>
            <HStack gap={4}>
              <RadioGroupFormControl control={control} name="signOption" valueAsNumber>
                <Stack>
                  {ContractSignOptions?.map(t => <Radio key={t[0]} value={t[0].toString()}>{t[1]}</Radio>)}
                </Stack>
              </RadioGroupFormControl>
              <DateFormControl control={control} name="dateOfSign" isRequired />
            </HStack>
          </Stack>
            :
            <root.div>
              <Box dangerouslySetInnerHTML={{ __html: acceptation }} />
            </root.div>}

          <root.div>
            <Box dangerouslySetInnerHTML={{ __html: documents }} />
          </root.div>

          <Box py={6}>
            <root.div>
              <Box dangerouslySetInnerHTML={{ __html: signature }} />
            </root.div>
          </Box>


          <Box margin='0 -24px -24px -24px' bg='#f2f4f8' padding="16px 24px" borderRadius='0 0 10px 10px'>
            <root.div>
              <Box dangerouslySetInnerHTML={{ __html: disclaimer }} />
            </root.div>
          </Box>
        </Stack>
      </Stack>
    </FormProvider>
  )
}